import * as React from 'react'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ResizableBox } from 'react-resizable'
import { Button, Checkbox, IModalProps, Modal, SearchBox, ToggleFilter, useAlert, useDebounce, useEventSubscriber, useToast } from 'uxp/components'
import { IContextProvider } from '../../uxp'
import { filterTemplates, getCardTemplates, IWidgetTemplate } from '../common/common'
import { hasValue, loadScriptFileAsync, tryParseJSON } from '../../util'
import classNames from 'classnames'
import './TemplateSelector.scss'

interface ITemplateSelectorProps extends IModalProps {
    uxpContext: IContextProvider,
    onSelectTemplate: (id: string, moduleId?: string) => void,
    expectedSchema?: any,
    exactSchemaMatch?: boolean,
}

const TemplateSelector: React.FunctionComponent<ITemplateSelectorProps> = (props) => {
    const { show, onClose, onOpen, uxpContext, onSelectTemplate } = props
    const [selected, setSelected] = React.useState(null)
    const [targetSchema, setTargetSchema] = React.useState<any>(props.expectedSchema)
    const [showAll, setShowAll] = React.useState(false)

    const [query, setQuery] = React.useState('')
    const [view, setView] = React.useState<'default' | 'custom' | 'user'>('default')
    const [allUserTemplates, setAllUserTemplates] = React.useState([])
    const [allCustomTemplates, setAllCustomTemplates] = React.useState([])

    const toast = useToast()
    const alerts = useAlert()
    const debouncedQuery = useDebounce(query, 300)

    useEventSubscriber('template-selector', 'save-as-template', (data) => {
        getAllUserTemplates()
    })

    React.useEffect(() => {
        getAllUserTemplates()
    }, [])
    React.useEffect(() => {
        setSelected(null)
    }, [props])
    React.useEffect(() => {
        if (showAll) setTargetSchema(null)
        else setTargetSchema(props.expectedSchema)

    }, [props.expectedSchema, showAll])

    React.useEffect(() => {
        getAllUserTemplates()
    }, [view])

    React.useEffect(() => {
    }, [allCustomTemplates])

    React.useEffect(() => {
    }, selected)

    function renderIcon(icon: IconProp | JSX.Element) {
        if (React.isValidElement(icon)) return icon
        return <FontAwesomeIcon style={{ width: '60px', height: '60px' }} icon={icon as IconProp} />
    }


    async function getAllUserTemplates() {

        const defaultTemplates = getCardTemplates()
        const customTemplates = await loadCustomWidgetTemplates()

        const allTemplates = defaultTemplates.concat(customTemplates)

        uxpContext.executeService('UXP', 'UserWidgetTemplate:AllTemplates', {}, { json: true })
            .then(res => {
                console.log('all templates ', res);

                const processed = res.map((r: any) => {
                    const { Name, Description, Template, Key, WidgetID } = r
                    const _template = tryParseJSON(Template)

                    console.log('template ', _template);
                    if (!_template) return null

                    // find default template 
                    const defInst = allTemplates.find(t => t.id == _template?.__template__?.type)
                    console.log('default template ', defInst)
                    if (!defInst) return null

                    const parts = WidgetID.split('/')
                    let moduleId = null
                    if (hasValue(parts[0]) && !parts[0].startsWith('uxp-widget-designer-')) moduleId = parts[0].trim()

                    return {
                        id: `user-template@@@${defInst.id}@@@${Key}`,
                        name: Name,
                        description: Description || defInst.description || '',
                        icon: defInst.icon || null,
                        expectedSchema: defInst.expectedSchema || null,
                        template: defInst.template,
                        defaultProps: _template.uiProps,
                        complexity: defInst.complexity,
                        key: Key,
                        moduleId: moduleId
                    } as IWidgetTemplate
                }).filter((r: any) => !!r)
                setAllUserTemplates(processed)
            })
            .catch(e => {
                console.log('unable to get all the user templates. ', e)
            })
    }


    function getUserTemplates(expectedSchema?: any, exactMatch: boolean = false, query?: string) {
        const filtered = filterTemplates(allUserTemplates, expectedSchema, exactMatch, query)
        return filtered
    }

    async function getAllCustomTemplates() {
        try {
            const templates = await uxpContext.executeService('UXP', 'Widget:GetAllTemplates', {}, { json: true })
            return templates
        } catch (e) {
            console.log('unable to get the custom widget templates. Exception: ', e);
            return []
        }
    }

    async function loadCustomWidgetTemplates() {
        const templates = await getAllCustomTemplates()
        for (const t of templates) {
            const parts = t.ID.split('/')
            const moduleId = parts[0]
            const widgetId = parts[1]

            if (!hasValue(moduleId) || !hasValue(widgetId)) continue;

            // load script file 
            if (moduleId == 'uxp-widget-designer-bundle') continue
            await loadScriptFileAsync(`/api/UXP/module?id=${moduleId}`, moduleId)
        }

        const registeredCustomTemplates = getCardTemplates(null, null, null, true)
        setAllCustomTemplates(registeredCustomTemplates)
        return registeredCustomTemplates
    }

    function getCustomTemplates(expectedSchema?: any, exactMatch: boolean = false, query?: string) {
        const filtered = filterTemplates(allCustomTemplates, expectedSchema, exactMatch, query)
        return filtered
    }


    const headerContent = <div className='header-content-container'>
        <div className="title">{'Select a Template '}</div>

        <div className="filters">

            <div className="filter">

                <SearchBox
                    value={query}
                    onChange={setQuery}
                />
            </div>
            <div className="filter">
                <ToggleFilter
                    value={view}
                    onChange={s => {
                        setView(s as any)
                        setSelected(null)
                    }}
                    options={[
                        { label: 'Default Templates', value: 'default' },
                        { label: 'Custom Templates', value: 'custom' },
                        { label: 'My Templates', value: 'user' }
                    ]}
                    className="lucy-canvas-toggle-button"
                />

            </div>

            {!!props.expectedSchema &&
                <Checkbox
                    checked={showAll}
                    onChange={setShowAll}
                    label='Show All Templates'
                />
            }

        </div>
    </div>


    async function deleteTemplate(key: string) {
        const confirm = await alerts.confirm('You are about to delete an template. This cannot be undone. Do you wish to continue?')

        if (confirm) {
            uxpContext.executeService('UXP', 'UserWidgetTemplate:Delete', { Key: key })
                .then(res => {
                    toast.success('Template Deleted!')
                    getAllUserTemplates()
                })
                .catch(e => {
                    toast.error('Unable to delete the template. Something went wrong')
                    console.log('error deleting template ', e);
                })
        }

        return
    }

    function renderTemplate(template: IWidgetTemplate, canDelete?: boolean) {
        return <div
            className={classNames('designer-item-thumbnail-container', { 'selected': template.id == selected?.id })}
            key={template.id}
            onClick={() => {
                if (selected?.id == template.id) setSelected(null)
                else setSelected(template)
            }}
        >
            <div className={classNames('designer-item-thumbnail')}>
                <div className='icon-container'>
                    <div className="icon-cont" >
                        {renderIcon(template.icon)}
                    </div>

                </div>

                <div className="description">
                    <div className="name">{template.name}</div>
                    <div className="text">
                        {template.description}
                    </div>
                </div>

                {canDelete &&
                    <div className="action">
                        <div className="delete" onClick={() => deleteTemplate(template.key)} >
                            <FontAwesomeIcon icon={['fas', 'trash']} />
                        </div>
                    </div>
                }
            </div>
        </div>
    }

    return <Modal
        className='template-selector-modal'
        show={show}
        onClose={onClose}
        onOpen={onOpen}
        title={"Select a Template "}
        headerContent={headerContent}
    >
        <div className="note">
            Click on a template to see a preview. Then click on the `Edit this template` button to start customizing
        </div>

        <div className={classNames("templates", { 'has-preview': !!selected })}>
            {view == 'default' ? <>
                {getCardTemplates(targetSchema, props.exactSchemaMatch, debouncedQuery).map(t => renderTemplate(t))}
            </>
                : <>
                    {
                        view == 'user' ? <>
                            {getUserTemplates(targetSchema, props.exactSchemaMatch, debouncedQuery).map(t => renderTemplate(t, true))}
                        </>
                            : <>
                                {getCustomTemplates(targetSchema, props.exactSchemaMatch, debouncedQuery).map(t => renderTemplate(t))}
                            </>
                    }
                </>
            }
        </div>



        {!!selected &&
            <>
                <div className={classNames("preview-panel")}>
                    <div className="container">
                        <div className="header">

                            <div className="title">{selected.name}</div>
                            <div className="spacer">
                                <div className={classNames("tag", selected.complexity || '')}>{selected.complexity || ''}</div>
                            </div>
                        </div>

                        <div className="description">
                            {selected.description}
                        </div>

                        <div className="body">
                            <ResizableBox
                                width={300} height={300}
                                minConstraints={[100, 100]}
                                maxConstraints={[700, 700]}
                            >
                                <div className={'wd-work-area-widget-container'}>
                                    {
                                        selected
                                            ? React.createElement(selected.template, { designer: { mode: 'thumbnail' }, uiProps: selected.defaultProps || {}, uxpContext: uxpContext })
                                            : <div> Please select a widget template</div>
                                    }
                                </div>
                            </ResizableBox>
                        </div>
                    </div>
                </div>

                <div className="footer">
                    <Button
                        title='Edit this template'
                        onClick={() => onSelectTemplate(selected.id, selected?.moduleId)}
                        className='edit-template-btn'
                    />

                </div>
            </>
        }
    </Modal>
}

export default TemplateSelector